// Shared route helpers for the linear comment-chain ("Read as conversation") view.
//
// The chain reuses the focused-thought permalink (/discussion/{slug}/thought/{id}, server-bootstrapped
// with the anchor as openingArgumentPublicId) plus a ?view=chain flag that switches the client render
// from the tree/card to the linear ThoughtChainDisplayer. Keeping it a query flag (not a new path
// segment) means the existing backend focused-thought route serves it unchanged and still provides the
// anchor. (The legacy /argument/ segment still 301-redirects here; the client emits the canonical
// /thought/ form after the Phase-4 follow-up-#4 URL-builder migration.)

export const CHAIN_VIEW_QUERY_KEY = "view";
export const CHAIN_VIEW_QUERY_VALUE = "chain";

/**
 * True when a path segment is too absent to build a URL from.
 *
 * <p>The literal strings matter as much as the nullish check. `encodeURIComponent(null)` returns the
 * **string** `"null"`, and a template literal stringifies `null`/`undefined` the same way — so a
 * missing id does not produce an obviously broken URL, it produces a well-formed, clickable,
 * permanently-404ing one. That is why this class survives review and reaches prod: nothing looks
 * wrong until someone reads the access log.
 */
function isUnusableSegment(value: string | null | undefined): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    const trimmed = value.trim();
    return trimmed === "" || trimmed === "null" || trimmed === "undefined";
}

/**
 * The canonical focused-thought permalink, or `null` when it cannot be built.
 *
 * <p>Returning `null` rather than a best-effort string is the point: a caller must decide to render a
 * non-navigating element, which is a decision the type system now forces. The guard lives in the
 * BUILDER, not at its call sites, for the same reason AGENTS.md rule 34 puts the feature check in the
 * URL builder — so the next surface inherits it instead of re-deriving it.
 *
 * <p>Prod evidence (2026-08-05): 15 requests in 12h to paths ending `/null`, all 404, whose `referer`
 * was one of our own discussion pages — i.e. we rendered the links. Every requester was a crawler,
 * so no member had hit one yet, but a link that 404s for a crawler 404s for the next person who
 * receives it in a share. `AccountAccessDialogContext.ts` already carried a note that "`/thought/null`
 * URLs reached prod", so this was a known class with live unguarded builders.
 */
export function thoughtPermalinkHref(
    discussionSlug: string | null | undefined,
    thoughtPublicId: string | null | undefined,
): string | null {
    if (isUnusableSegment(discussionSlug) || isUnusableSegment(thoughtPublicId)) {
        return null;
    }
    return `/discussion/${encodeURIComponent(discussionSlug!.trim())}`
        + `/thought/${encodeURIComponent(thoughtPublicId!.trim())}`;
}

/**
 * The bare `/thought/{id}` form, or `null` when the id cannot be built from.
 *
 * <p>This is a REAL backend route, not a fallback shape invented on the client:
 * `DiscussionConversationController.getArgument` resolves the thought's discussion and 302s to the
 * slug-qualified permalink above, and `WebSecurityConfig` allows `/thought/**` anonymously. It is the
 * right thing to emit when a row knows the thought but not its discussion slug — the server knows.
 *
 * <p>It lives here rather than at the one call site for the same reason `thoughtPermalinkHref` does:
 * the id needs the identical `isUnusableSegment` guard, and a second copy of that predicate is how the
 * two shapes drift. An absent id here produces a bare `/thought/`, which 404s exactly as
 * `/thought/null` did.
 */
export function thoughtShortRedirectHref(thoughtPublicId: string | null | undefined): string | null {
    if (isUnusableSegment(thoughtPublicId)) {
        return null;
    }
    return `/thought/${encodeURIComponent(thoughtPublicId!.trim())}`;
}

/** Permalink that opens the linear chain anchored at a given thought, or `null` if unbuildable. */
export function thoughtChainHref(
    discussionSlug: string | null | undefined,
    anchorArgumentPublicId: string | null | undefined,
): string | null {
    const base = thoughtPermalinkHref(discussionSlug, anchorArgumentPublicId);
    return base === null
        ? null
        : `${base}?${CHAIN_VIEW_QUERY_KEY}=${CHAIN_VIEW_QUERY_VALUE}`;
}

/** True when the current URL requests the chain view (read from window.location at call time). */
export function isChainViewRequested(): boolean {
    if (typeof window === "undefined") {
        return false;
    }
    try {
        return new URLSearchParams(window.location.search).get(CHAIN_VIEW_QUERY_KEY) === CHAIN_VIEW_QUERY_VALUE;
    } catch {
        return false;
    }
}
